import { metres, type Spot } from '@day';
import { lookup, LookupError } from './maps';
import { decode, encode } from './polyline';

/**
 * A route's order and times, as the phone works them out (RoutesApiOptimizer, HaversineOptimizer): Google's Routes API
 * through Routr's server when it answers, else straight lines at 30 km/h, which the phone falls back to as well. The
 * result is written as the phone's `OptimizationResult.toJson()`, so a courier's phone reads it unchanged.
 */

export interface Timed {
  /** Stop ids in the order they're visited. */
  order: string[];
  /** Driving into each stop (seconds): from the start for the first. */
  legS: number[];
  distanceM: number;
  drivingS: number;
  polyline: string | null;
  method: 'routesApi' | 'haversine';
  /** Why it's straight lines, when it is. */
  why?: string;
  /** Back to start: the drive from the last stop back to the start (seconds); counted in [distanceM] and [drivingS]. */
  backS?: number;
}

interface Point extends Spot {
  id: string;
}

/** About how long [m] metres of straight line takes to drive (`HaversineOptimizer.roughDrive`: 30 km/h). */
export const roughDrive = (m: number) => Math.round(m / ((30 * 1000) / 3600));

/** Nearest stop next, from the start (the phone's straight-line order). */
export function nearestOrder(start: Spot | null, stops: Point[]): Point[] {
  const left = [...stops];
  const out: Point[] = [];
  let at: Spot | null = start;
  while (left.length) {
    // Nearest to where the last one left off (the start for the first).
    let bi = 0;
    if (at) {
      let bd = Infinity;
      left.forEach((x, i) => {
        const d = metres(at!, x);
        if (d < bd) {
          bd = d;
          bi = i;
        }
      });
    }
    const next = left.splice(bi, 1)[0];
    out.push(next);
    at = next;
  }
  return out;
}

/** Straight lines, in [ordered] order; [back]: and from the last stop back to the start. */
export function straight(start: Spot | null, ordered: Point[], why?: string, back = false): Timed {
  const legS = ordered.map((s, i) => (i === 0 ? (start ? roughDrive(metres(start, s)) : 0) : roughDrive(metres(ordered[i - 1], s))));
  let distanceM = Math.round(ordered.reduce((d, s, i) => d + (i === 0 ? (start ? metres(start, s) : 0) : metres(ordered[i - 1], s)), 0));
  const home = back && start && ordered.length ? metres(ordered.at(-1)!, start) : 0;
  distanceM += Math.round(home);
  const backS = back && start && ordered.length ? roughDrive(home) : undefined;
  return { order: ordered.map((s) => s.id), legS, distanceM, drivingS: legS.reduce((a, b) => a + b, 0) + (backS ?? 0), polyline: null, method: 'haversine', why, ...(backS != null ? { backS } : {}) };
}

/** The most stops Google's Routes API takes between a route's ends (batches.dart `maxStopsPerRequest`). */
export const MAX_PER_REQUEST = 25;

/**
 * [ordered] cut into consecutive batches of at most [max] stops, as even as possible (30 → 15 + 15, not 25 + 5): a small
 * last batch leaves Google little to sort (batches.dart `batchesOf`).
 */
export function batchesOf<T>(ordered: T[], max = MAX_PER_REQUEST): T[][] {
  if (ordered.length <= max) return [ordered];
  const n = Math.ceil(ordered.length / max);
  const base = Math.floor(ordered.length / n);
  const extra = ordered.length % n;
  const out: T[][] = [];
  let at = 0;
  for (let i = 0; i < n; i++) {
    const size = base + (i < extra ? 1 : 0);
    out.push(ordered.slice(at, at + size));
    at += size;
  }
  return out;
}

/**
 * [ordered] with its crossings taken out (2-opt, batches.dart `untangle`): a stretch turned round wherever that's shorter
 * as the crow flies, until no turn helps. Without a [start] the first stop stays first (the route leaves from it).
 */
export function untangle<T extends Spot>(ordered: T[], start: Spot | null, maxPasses = 30): T[] {
  const fixed = start ? 0 : 1;
  const r = [...ordered];
  if (r.length - fixed < 3) return r;
  const before = (i: number) => (i > 0 ? r[i - 1] : start);
  const link = (a: Spot | null, b: Spot) => (a ? metres(a, b) : 0);
  for (let pass = 0; pass < maxPasses; pass++) {
    let improved = false;
    for (let i = fixed; i < r.length - 1; i++) {
      for (let k = i + 1; k < r.length; k++) {
        const p = before(i);
        const q = k < r.length - 1 ? r[k + 1] : null;
        const now = link(p, r[i]) + (q ? metres(r[k], q) : 0);
        const turned = link(p, r[k]) + (q ? metres(r[i], q) : 0);
        if (turned < now - 0.5) {
          for (let lo = i, hi = k; lo < hi; lo++, hi--) [r[lo], r[hi]] = [r[hi], r[lo]];
          improved = true;
        }
      }
    }
    if (!improved) break;
  }
  return r;
}

/**
 * One route from batches routed one after another (batches.dart `joinBatches`), each from the last stop of the one before:
 * stops, distance and time add up; each later batch's first leg is the drive across from the one before; Google's line is
 * joined when every batch has one; straight lines anywhere make the whole route straight lines, with the reason.
 */
export function joinBatches(parts: Timed[]): Timed {
  if (parts.length === 1) return parts[0];
  const straightOne = parts.find((p) => p.method === 'haversine');
  const lines = parts.map((p) => p.polyline);
  return {
    order: parts.flatMap((p) => p.order),
    legS: parts.flatMap((p) => p.legS),
    distanceM: parts.reduce((a, p) => a + p.distanceM, 0),
    drivingS: parts.reduce((a, p) => a + p.drivingS, 0),
    polyline: lines.every((l) => !!l) ? encode(lines.flatMap((l) => decode(l!))) : null,
    method: straightOne ? 'haversine' : 'routesApi',
    ...(straightOne?.why ? { why: straightOne.why } : {}),
    ...(parts.at(-1)!.backS != null ? { backS: parts.at(-1)!.backS } : {}),
  };
}

const seconds = (s: unknown) => {
  const m = /^(\d+)s$/.exec(String(s ?? ''));
  return m ? Number(m[1]) : 0;
};

const waypoint = (p: Spot) => ({ location: { latLng: { latitude: p.lat, longitude: p.lng } } });

/**
 * Orders and times [stops] from [start]. [optimise]: Google chooses the order of the stops between the first and the
 * last (the phone's "Sort"); otherwise the order given is kept and only timed again (Reverse, a new order).
 */
export async function timeRoute(start: Spot | null, stops: Point[], { optimise, back = false, keepEnds = false }: { optimise: boolean; back?: boolean; keepEnds?: boolean }): Promise<Timed> {
  if (!stops.length) return { order: [], legS: [], distanceM: 0, drivingS: 0, polyline: null, method: 'haversine' };
  // [keepEnds]: a batch of a longer route, whose last stop is where the next batch starts: only Google orders its middle.
  const given = optimise && !keepEnds ? nearestOrder(start, stops) : stops;
  // Back to start: every stop sits between the ends, so Google may choose the whole order.
  const round = back && !!start;
  // The Routes API takes 25 stops between the ends: a longer route is put in order here first (nearest next, untangled),
  // cut into even batches, and each goes to Google at once from where the one before it ends (batches.dart).
  if (given.length > (round ? MAX_PER_REQUEST : MAX_PER_REQUEST + 1)) {
    const ordered = optimise ? untangle(given, start) : given;
    const parts = batchesOf(ordered);
    const timed = await Promise.all(parts.map((b, i) => timeRoute(i === 0 ? start : parts[i - 1].at(-1)!, b, { optimise, back: round && i === parts.length - 1, keepEnds: true })));
    return joinBatches(timed);
  }
  const origin = start ?? given[0];
  const intermediates = round ? given : start ? given.slice(0, -1) : given.slice(1, -1);
  const destination = round ? { id: '__start', ...start! } : given.at(-1)!;
  if (!start && given.length === 1) return straight(start, given);
  try {
    const data = await lookup('routes', {
      body: {
        origin: waypoint(origin),
        destination: waypoint(destination),
        intermediates: intermediates.map(waypoint),
        travelMode: 'DRIVE',
        routingPreference: 'TRAFFIC_AWARE',
        optimizeWaypointOrder: optimise && intermediates.length > 1,
        polylineQuality: 'OVERVIEW',
        computeAlternativeRoutes: false,
      },
    });
    const route = (Array.isArray(data.routes) ? data.routes[0] : null) as Record<string, unknown> | null;
    if (!route) return straight(start, given, 'Google found no road route between these stops');
    const idx = route.optimizedIntermediateWaypointIndex;
    const isOrder = Array.isArray(idx) && idx.length === intermediates.length && new Set(idx).size === idx.length && idx.every((i) => typeof i === 'number' && i >= 0 && i < intermediates.length);
    const middle = isOrder ? (idx as number[]).map((i) => intermediates[i]) : intermediates;
    const ordered = round ? middle : start ? [...middle, destination] : [given[0], ...middle, destination];
    const legs = (Array.isArray(route.legs) ? route.legs : []).map((l) => seconds((l as Record<string, unknown>).duration));
    const needed = round ? ordered.length + 1 : start ? ordered.length : ordered.length - 1;
    if (legs.length < needed) return straight(start, ordered, 'Google’s answer was missing legs', back);
    const legS = start ? legs.slice(0, ordered.length) : [0, ...legs.slice(0, ordered.length - 1)];
    const poly = route.polyline as { encodedPolyline?: string } | undefined;
    return {
      order: ordered.map((s) => s.id),
      legS,
      distanceM: Number(route.distanceMeters ?? 0) | 0,
      drivingS: seconds(route.duration),
      polyline: poly?.encodedPolyline ?? null,
      method: 'routesApi',
      ...(round ? { backS: legs[ordered.length] } : {}),
    };
  } catch (e) {
    return straight(start, given, e instanceof LookupError ? e.message : 'Routr’s server didn’t answer', back);
  }
}

/** The phone's `OptimizationResult.toJson()`: what a sent route keeps as `optimization`. */
export function optimizationJson(t: Timed, fromStart: boolean): Record<string, unknown> {
  return {
    ordered_stop_ids: t.order,
    total_distance_m: Math.round(t.distanceM),
    total_duration_s: Math.round(t.drivingS),
    method: t.method,
    optimized_at: new Date().toISOString(),
    polyline: t.polyline,
    from_start: fromStart,
    leg_s: t.legS.map((n) => Math.round(n)),
    // Back to start (the phone's round trip): the way back, timed. The courier's phone keeps its own setting for it.
    ...(t.backS != null ? { round_trip: true, back_s: Math.round(t.backS) } : {}),
  };
}

/** First, Auto or Last on a stop (the phone's OrderHint): where it sits in any order Routr makes. */
export type Hint = 'first' | 'auto' | 'last';

/**
 * [ids] with a pickup before each delivery it feeds ([pickupOf]: delivery → its pickup), as PickupOrder keeps it: a
 * delivery that comes first takes its pickup to just before it. Everything else keeps its place.
 */
export function pickupsBefore(ids: string[], pickupOf: Map<string, string>): string[] {
  const out = [...ids];
  for (let i = 0; i < out.length; i++) {
    const p = pickupOf.get(out[i]);
    const j = p ? out.indexOf(p) : -1;
    if (j > i) {
      out.splice(j, 1);
      out.splice(i, 0, p!);
      i++;
    }
  }
  return out;
}

/**
 * [ids] with each stop in [places] (id → its place in the order, from 1) at that place and the rest round it in the order
 * they had. Past the end means last; a place already taken, the next one free.
 */
export function atPlaces(ids: string[], places: Map<string, number>): string[] {
  const pinned = ids.filter((id) => places.has(id)).sort((a, b) => places.get(a)! - places.get(b)!);
  if (!pinned.length) return ids;
  const out: (string | null)[] = ids.map(() => null);
  for (const id of pinned) {
    const wanted = Math.min(places.get(id)!, ids.length) - 1;
    let i = wanted;
    while (i < out.length && out[i]) i++;
    if (i === out.length) for (i = wanted; out[i]; i--);
    out[i] = id;
  }
  const rest = ids.filter((id) => !places.has(id));
  return out.map((id) => id ?? rest.shift()!);
}

/**
 * Sort, as the phone's RoutingService does: stops pinned First keep their order at the start and stops pinned Last at the
 * end; Google chooses the order of the rest (through Routr's server), then a stop given a number goes to its place, a pickup
 * is put before its delivery (which can move a number on by one) and the whole order is timed. Straight lines when Google
 * can't answer. [back]: the route comes back to the start.
 */
export async function sortStops(start: Spot | null, stops: (Point & { hint?: Hint; pickupId?: string | null; place?: number | null })[], back = false): Promise<Timed> {
  const free = stops.filter((x) => x.place == null);
  const firsts = free.filter((x) => x.hint === 'first');
  const lasts = free.filter((x) => x.hint === 'last');
  const autos = free.filter((x) => x.hint !== 'first' && x.hint !== 'last');
  const pickupOf = new Map(stops.filter((x) => x.pickupId).map((x) => [x.id, x.pickupId!] as [string, string]));
  const byId = new Map(stops.map((x) => [x.id, x]));
  const fix = (ids: string[]) => pickupsBefore(ids, pickupOf);
  if (free.length < stops.length) {
    const places = new Map(stops.filter((x) => x.place != null).map((x) => [x.id, x.place!] as [string, number]));
    const from = firsts.at(-1) ?? start;
    const middle = autos.length > 1 ? (await timeRoute(from, autos, { optimise: true })).order : autos.map((x) => x.id);
    const ids = fix(atPlaces([...firsts.map((x) => x.id), ...middle, ...lasts.map((x) => x.id), ...places.keys()], places));
    return timeRoute(start, ids.map((id) => byId.get(id)!), { optimise: false, back });
  }
  if (!firsts.length && !lasts.length) {
    const t = await timeRoute(start, autos, { optimise: true, back });
    const fixed = fix(t.order);
    return fixed.join() === t.order.join() ? t : timeRoute(start, fixed.map((id) => byId.get(id)!), { optimise: false, back });
  }
  const from = firsts.at(-1) ?? start;
  const middle = autos.length > 1 ? (await timeRoute(from, autos, { optimise: true })).order : autos.map((x) => x.id);
  const ids = fix([...firsts.map((x) => x.id), ...middle, ...lasts.map((x) => x.id)]);
  return timeRoute(start, ids.map((id) => byId.get(id)!), { optimise: false, back });
}

/** When each stop is reached if the route leaves at [at], and when it's done: the drive into each, and [dwellS] at each. */
export function estimates(t: Pick<Timed, 'legS' | 'backS'>, at: number, dwellS: number): { arrive: number[]; finish: number } {
  const arrive: number[] = [];
  let clock = at;
  t.legS.forEach((leg, i) => {
    clock += (leg + (i > 0 ? dwellS : 0)) * 1000;
    arrive.push(clock);
  });
  const finish = (arrive.at(-1) ?? at) + (t.legS.length ? dwellS * 1000 : 0) + (t.backS ?? 0) * 1000;
  return { arrive, finish };
}
